import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, Space, Table, Form, Input, message, Typography } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import SH_Toolbar from '../../components/SH_Toolbar';
import TV_user_detail from './TV_user_detail';
import Title from 'antd/lib/typography/Title';
import dayjs from 'dayjs';
import userApi from '../../api/user';

const { Text } = Typography;

const TV_user = () => {
  const [dictionary, setDictionary] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const refDetail = useRef();

  const user = JSON.parse(localStorage.getItem('user'));
  const [filter, setFilter] = useState({});

  // Reset password modal state
  const [resetOpen, setResetOpen] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetForm] = Form.useForm();

  useEffect(() => {
    getUser();
  }, []);

  const getUser = (_filter) => {
    setLoading(true);
    userApi
      .get({
        limit: _filter?.limit || 10,
        page: _filter?.page || 1,
      })
      .then((res) => {
        setProducts(res.data.data?.items?.filter((x) => x.email !== user.email) || []);
        setTotal(res.data.data?.pagination?.total || 0);
      })
      .catch((e) => {
        console.error(e);
        message.error('Không tải được danh sách user');
      })
      .finally(() => setLoading(false));
  };

  const openResetModal = (record) => {
    setResetUser(record);
    resetForm.resetFields();
    setResetOpen(true);
  };

  const closeResetModal = () => {
    setResetOpen(false);
    setResetUser(null);
    resetForm.resetFields();
  };

  const submitResetPassword = async (values) => {
    const id = resetUser?.id || resetUser?._id;
    if (!id) return message.error('Không tìm thấy id user');

    try {
      setResetLoading(true);
      await userApi.resetPassword(id, {
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      });
      message.success('Đã reset mật khẩu');
      closeResetModal();
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.message || 'Reset mật khẩu thất bại');
    } finally {
      setResetLoading(false);
    }
  };

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'Vai trò',
      dataIndex: 'isAdmin',
      key: 'isAdmin',
      render: (text) => (
        <Button danger={text} size="small">
          {text ? 'Admin' : 'Người dùng'}
        </Button>
      ),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt) => (
        <span>{createdAt ? dayjs(createdAt).format('HH:mm A DD/MM/YYYY') : ''}</span>
      ),
      sorter: (a, b) => dayjs(a.createdAt).isAfter(dayjs(b.createdAt)),
      sortDirections: ['descend'],
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button onClick={() => refDetail.current.update(record)} icon={<EditOutlined />}>
            Sửa
          </Button>

          <Button
            onClick={() => openResetModal(record)}
            icon={<KeyOutlined />}
            type="dashed"
          >
            Đổi mật khẩu
          </Button>

          <Button onClick={() => conFirm(record)} type="dashed" danger icon={<DeleteOutlined />}>
            Xoá
          </Button>
        </Space>
      ),
    },
  ];

  const conFirm = (event) => {
    return Modal.confirm({
      title: 'Xác nhận xóa',
      content: 'Bạn có chắc chắn muốn xóa bản ghi này không ? ',
      onOk: () => {
        deleteItem(event);
      },
      footer: (_, { OkBtn, CancelBtn }) => (
        <>
          <CancelBtn />
          <OkBtn />
        </>
      ),
    });
  };

  const deleteItem = (event) => {
    setLoading(true);
    userApi
      .delete(event.id || event._id)
      .then(() => getUser())
      .catch((e) => {
        console.error(e);
        message.error('Xoá thất bại');
      })
      .finally(() => setLoading(false));
  };

  const showModal = () => {
    refDetail.current.create();
  };

  const handlePageChange = (page, pageSize) => {
    setFilter((prevState) => ({
      ...prevState,
      page,
      limit: pageSize,
    }));
    getUser({ page, limit: pageSize });
  };

  return (
    <>
      <Title level={2}>Người dùng</Title>

      <SH_Toolbar
        left={() => (
          <Button icon={<PlusOutlined />} type="link" onClick={showModal}>
            Thêm
          </Button>
        )}
        right={() => <></>}
      />

      <Table
        rowKey={(r) => r.id || r._id}
        pagination={{
          current: filter.page,
          pageSize: filter.limit,
          total: total,
          onChange: handlePageChange,
        }}
        className="mt-2"
        loading={loading}
        columns={columns}
        dataSource={products}
        scroll={{ x: 1300 }}
      />

      <TV_user_detail
        ref={refDetail}
        getUser={getUser}
        categories={dictionary?.categories}
        colors={dictionary.colors}
      />

      {/* Modal reset mật khẩu */}
      <Modal
        open={resetOpen}
        title="Đổi mật khẩu người dùng"
        okText="Cập nhật"
        cancelText="Huỷ"
        confirmLoading={resetLoading}
        onOk={() => resetForm.submit()}
        onCancel={closeResetModal}
        destroyOnHidden
      >
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">
            User: <b>{resetUser?.email || '-'}</b>
          </Text>
        </div>

        <Form
          form={resetForm}
          layout="vertical"
          onFinish={submitResetPassword}
        >
          <Form.Item
            label="Mật khẩu mới"
            name="newPassword"
            rules={[
              { required: true, message: 'Vui lòng nhập mật khẩu mới' },
              { min: 6, message: 'Tối thiểu 6 ký tự' },
            ]}
          >
            <Input.Password placeholder="Nhập mật khẩu mới" />
          </Form.Item>

          <Form.Item
            label="Xác nhận mật khẩu mới"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Vui lòng xác nhận mật khẩu' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                  return Promise.reject(new Error('Xác nhận mật khẩu không khớp'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Nhập lại mật khẩu mới" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default TV_user;
